import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const QuoteClosingSlide = ({ practiceName, collections, patients, ashaPrice, providers, planType }) => {
  const scale = collections / 100000;
  const ptScale = patients / 300;
  const staffFactor = (scale * 0.5 + ptScale * 0.5);
  const isFlexible = !planType || planType === "flexible";
  const isManaged = planType === "managed";
  const isFixed = planType === "fixed";

  const perPatient = ashaPrice ? (ashaPrice / patients).toFixed(2) : null;
  const costRatio = perPatient ? Math.round(120 / Number(perPatient)) : null;

  let billing = 0;
  if (collections <= 100000) billing = collections * 0.035;
  else if (collections <= 150000) billing = 100000 * 0.035 + (collections - 100000) * 0.03;
  else billing = 100000 * 0.035 + 50000 * 0.03 + (collections - 150000) * 0.025;
  const rcm = Math.round(billing) + patients * 8 + 1200;
  const sw = 3298 + Math.max(0, (providers || 2) - 2) * 149;
  const labor = Math.round(scale * 0.5 * 3500);
  const piecemealTotal = rcm + sw + labor;
  const savings = ashaPrice ? piecemealTotal - ashaPrice : piecemealTotal;

  const prodR = Math.round(8000 * scale * (0.6 + ptScale * 0.4));
  const ncrR = Math.round(collections * 0.02);
  const staffR = Math.round(3500 * staffFactor);
  const swR = Math.round(3000 * Math.max(1, 0.7 + scale * 0.3));
  const totalROI = prodR + ncrR + staffR + swR;

  let point2Body;
  if (isFlexible && ashaPrice) {
    const tierPerPatient = patients <= 300 ? 15 : patients <= 500 ? 12 : 8;
    point2Body = `At $${tierPerPatient}/patient, your effective cost is $${perPatient}/patient across ${patients} patients/mo. Your staff costs $120+ per patient journey — Asha does it for 1/${costRatio}th the cost. Piecemealing the same coverage would run ~$${piecemealTotal.toLocaleString()}/mo. Asha saves you $${savings.toLocaleString()}/mo from day one.`;
  } else if (isFixed && ashaPrice) {
    point2Body = `At $${ashaPrice.toLocaleString()}/mo fixed, your effective cost is $${perPatient}/patient across ${patients} patients/mo. Your staff costs $120+ per patient journey — Asha does it for 1/${costRatio}th the cost. Piecemealing the same coverage would run ~$${piecemealTotal.toLocaleString()}/mo. That's $${savings.toLocaleString()}/mo in savings from day one.`;
  } else if (isManaged && ashaPrice) {
    const pctOfCollections = ((ashaPrice / collections) * 100).toFixed(1);
    point2Body = `At $799/mo + 3% of collections (${pctOfCollections}% effective), you pay less than billing companies charge (3.5–5%) — and they only handle claims. Piecemealing the same coverage would cost ~$${piecemealTotal.toLocaleString()}/mo. Asha saves you $${savings.toLocaleString()}/mo, at $${perPatient}/patient vs $120+ with humans.`;
  } else {
    point2Body = `Your staff costs $120+ per patient journey. Piecemealing equivalent coverage would cost ~$${piecemealTotal.toLocaleString()}/mo. Asha does all of it for a fraction.`;
  }

  const points = [
    {
      title: "Has a full AI OS for dental — 10 softwares, 20 AI products, 1 platform.",
      body: `No more stitching together 8–12 vendors that were never designed to work together. Asha is built AI-native from the ground up — every product shares one data layer, one patient record, one workflow engine. Your receptionist AI, billing AI, recall AI, and practice manager AI all talk to each other natively. No middleware, no syncing, no gaps.`,
    },
    {
      title: "Offers pricing so good that no other vendor can match.",
      body: point2Body,
    },
    {
      title: "Is powered by ARIA, a deep reasoning breakthrough no vendor in dentistry has access to.",
      body: `ARIA is Asha's proprietary AI reasoning engine purpose-built for dental workflows — not a chatbot wrapper or a generic LLM bolted onto a legacy system. It's the core intelligence enabling 20 AI products to operate as one coordinated system. No other company in dentistry has anything comparable. Comparisons to point-solution vendors are apples to oranges — this is a category of one.`,
    },
    {
      title: "Doesn't just improve 1 metric — transforms your economics across the board.",
      body: `AI drives new production through after-hours booking, hygiene reactivation, and case acceptance follow-up. Claim scrubbing and automated AR recovery improve your net collection rate. Staff hours on insurance calls, verification, and billing get redirected to chairside care. Software consolidation eliminates ~$${sw.toLocaleString()}/mo in vendor costs. Potential combined impact for ${practiceName}: ~$${totalROI.toLocaleString()}/mo.`,
    },
    {
      title: "Transforms your practice into an AI clinic that runs on autopilot.",
      body: `30–40% annual front office turnover. $4,500+ per hire, 3–6 months to train. Asha's AI never quits, runs 24/7, handles ${patients} patients/mo with zero capacity constraints. Insurance verification in seconds vs 8–12 min. Claim scrubbing catches 15–20% of errors humans miss. Every AI product coordinates in real time — fewer dropped balls, faster collections, complete peace of mind.`,
    },
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-8 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="relative z-10 flex flex-col h-full w-full max-w-[1100px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 flex-shrink-0"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-2 border border-blue-100">
            <Sparkles className="w-4 h-4" />
            <span>Why Asha</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
            Asha is the <span className="text-blue-600">only company in the world</span> that...
          </h1>
        </motion.div>

        {/* Points */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-5">
          {points.map((pt, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.08 }}
              className="flex gap-4 items-start"
            >
              <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-[15px] font-extrabold">{i + 1}</span>
              </div>
              <div>
                <h3 className="text-[20px] font-bold text-slate-900 leading-snug mb-1">
                  {pt.title}
                </h3>
                <p className="text-[14px] text-slate-500 leading-relaxed">
                  {pt.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-3 text-center flex-shrink-0"
        >
          <p className="text-slate-400 text-sm">
            Ready to transform {practiceName}? Let's get started.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default QuoteClosingSlide;
